import { client } from './generated/client.gen';

const XSRF_COOKIE = 'XSRF-TOKEN';
const XSRF_HEADER = 'X-XSRF-TOKEN';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(prefix))
    ?.slice(prefix.length);
}

/**
 * The API is same-origin — served by ASP.NET Core in production, proxied by Vite in development —
 * so the session cookie rides along and there is no CORS anywhere.
 */
client.setConfig({
  baseUrl: '',
  credentials: 'same-origin',
});

/**
 * Attach the antiforgery token to every state-changing request.
 *
 * The token is read from the cookie on each request rather than cached, because the API re-issues
 * it whenever the signed-in identity changes. Caching it means the first write after login fails
 * with a 400 that explains nothing.
 */
client.interceptors.request.use((request) => {
  if (SAFE_METHODS.has(request.method)) {
    return request;
  }

  const token = readCookie(XSRF_COOKIE);
  if (token) {
    request.headers.set(XSRF_HEADER, decodeURIComponent(token));
  }

  return request;
});

/**
 * Fetches a fresh antiforgery token on app start. The API refreshes the cookie by itself after
 * login and logout, so this is only about the token we begin with.
 *
 * Deliberately unconditional. Holding a cookie is not the same as holding a *valid* one: the
 * token is encrypted with the server's data protection keys, so a key ring the server no longer
 * has — a deploy that did not persist it, a restore onto a fresh volume — leaves a cookie that
 * looks present and validates as garbage. Skipping the fetch because a cookie exists is how that
 * becomes an unexplained 400 on the next write, cleared only by emptying cookies by hand. One GET
 * per app load buys the guarantee.
 */
export async function ensureAntiforgeryToken(): Promise<void> {
  await fetch('/api/v1/antiforgery/token', { credentials: 'same-origin' });
}

export { client };
